import { AbstractControl } from '@angular/forms';
import { Observable, ReplaySubject } from 'rxjs';
import { distinctUntilChanged, map, tap } from 'rxjs/operators';
import { AnalyticsEvent, AnalyticsEventMap, AnalyticsEventName } from '../analytics/analytics.events';
import { FeatureFlag } from '../feature-flags/feature-flag.model';
import { collectInvalidPaths } from './form-invalid-paths';
import { productArea } from './route-pattern';

export interface PosthogClient {
  init(apiKey: string, config: Record<string, unknown>): unknown;
  capture(event: string, props?: Record<string, unknown>): unknown;
  identify(distinctId: string, props?: Record<string, unknown>): void;
  group(groupType: string, groupKey: string, props?: Record<string, unknown>): void;
  register(props: Record<string, unknown>): void;
  reset(): void;
  isFeatureEnabled(key: string): boolean | undefined;
  onFeatureFlags(callback: () => void): () => void;
}

export interface PosthogUser {
  id: string;
  email: string;
  name?: string;
  shopId: string;
  roles: string[];
}

export interface PosthogShop {
  id: number;
  name: string;
}

export interface PosthogTrackerOptions {
  env: string;
}

interface PageviewInput {
  url: string;
  routePattern: string;
  title: string;
}

const SHOP_GROUP = 'shop';
const INTERNAL_DOMAINS = new Set(['pdq.team', 'pdq.app', 'prettydamnquick.com']);

const URL_PROPERTIES = ['$current_url', '$referrer', '$initial_current_url', '$initial_referrer'];

export function stripUrlQuery(url: string): string {
  return url.split(/[?#]/)[0];
}

export function sanitizeProperties(properties: Record<string, unknown>): Record<string, unknown> {
  for (const key of URL_PROPERTIES) {
    if (typeof properties[key] === 'string') properties[key] = stripUrlQuery(properties[key] as string);
  }
  for (const bucket of ['$set', '$set_once']) {
    const nested = properties[bucket];
    if (nested && typeof nested === 'object') sanitizeProperties(nested as Record<string, unknown>);
  }
  return properties;
}

export function isInternalEmail(email: string | null | undefined): boolean {
  const domain = email?.trim().toLowerCase().split('@')[1];
  return !!domain && INTERNAL_DOMAINS.has(domain);
}

export class PosthogTracker {
  currentRoutePattern = '/';

  private isEnabled = false;
  private identityKey: string | null = null;
  private warned = false;
  private readonly flagsLoaded$ = new ReplaySubject<void>(1);

  constructor(
    private readonly client: PosthogClient,
    private readonly options: PosthogTrackerOptions,
  ) {}

  get enabled(): boolean {
    return this.isEnabled;
  }

  init(apiKey: string, host: string): void {
    if (this.isEnabled) return;
    if (!apiKey) {
      this.flagsLoaded$.next();
      return;
    }
    this.isEnabled = true;
    this.safely(() => {
      this.client.init(apiKey, {
        api_host: host,
        autocapture: true,
        capture_pageview: false,
        capture_pageleave: true,
        enable_heatmaps: true,
        session_recording: { maskAllInputs: true, maskTextSelector: '[data-ph-mask], [data-ph-mask] *' },
        persistence: 'localStorage+cookie',
        person_profiles: 'identified_only',
        sanitize_properties: sanitizeProperties,
      });
      this.client.onFeatureFlags(() => {
        if (this.identityKey) this.flagsLoaded$.next();
      });
    });
    if (!this.isEnabled) this.flagsLoaded$.next();
  }

  setUser(user: PosthogUser | null): void {
    if (!this.isEnabled) return;
    if (!user) {
      this.identityKey = null;
      this.safely(() => this.client.reset());
      return;
    }
    const key = `${user.id}|${user.shopId}`;
    if (key === this.identityKey) return;
    const sameUser = this.identityKey?.startsWith(`${user.id}|`) ?? false;
    this.identityKey = key;
    const isInternal = isInternalEmail(user.email);

    this.safely(() => {
      if (!sameUser) {
        this.client.identify(user.id, { email: user.email, name: user.name, roles: user.roles, isInternal });
      }
      this.client.group(SHOP_GROUP, user.shopId, { shopId: Number(user.shopId) });
      this.client.register({ shopId: Number(user.shopId), env: this.options.env, isInternal });
    });
  }

  setShop(shop: PosthogShop): void {
    if (!this.isEnabled) return;
    this.safely(() => {
      this.client.group(SHOP_GROUP, String(shop.id), { shopId: shop.id, shopName: shop.name });
      this.client.register({ shopName: shop.name });
    });
  }

  pageview({ url, routePattern, title }: PageviewInput): void {
    this.currentRoutePattern = routePattern;
    if (!this.isEnabled) return;
    this.safely(() =>
      this.client.capture('$pageview', {
        $current_url: stripUrlQuery(url),
        routePattern,
        title,
        productArea: productArea(routePattern),
      }),
    );
  }

  isFeatureEnabled(flag: FeatureFlag): boolean {
    let enabled = false;
    if (this.isEnabled) this.safely(() => (enabled = this.client.isFeatureEnabled(flag) === true));
    return enabled;
  }

  isFeatureEnabled$(flag: FeatureFlag): Observable<boolean> {
    return this.flagsLoaded$.pipe(
      map(() => this.isFeatureEnabled(flag)),
      distinctUntilChanged(),
    );
  }

  capture<N extends AnalyticsEventName>(name: N, props: AnalyticsEventMap[N]): void {
    if (!this.isEnabled) return;
    this.safely(() => this.client.capture(name, props));
  }

  trackSave<T>(form: AbstractControl, openedAt: number, save$: Observable<T>): Observable<T> {
    const routePattern = this.currentRoutePattern;
    const msSinceOpen = Math.round(performance.now() - openedAt);
    this.capture(AnalyticsEvent.SaveAttempted, { routePattern, msSinceOpen });
    if (form.invalid) {
      this.capture(AnalyticsEvent.ValidationFailed, { routePattern, fields: collectInvalidPaths(form) });
    }
    return save$.pipe(
      tap({
        next: () => this.capture(AnalyticsEvent.SaveSucceeded, { routePattern, msSinceOpen }),
        error: (err: { status?: number } | null) =>
          this.capture(AnalyticsEvent.SaveFailed, { routePattern, msSinceOpen, status: err?.status }),
      }),
    );
  }

  private safely(fn: () => void): void {
    try {
      fn();
    } catch (err) {
      if (!this.warned) {
        this.warned = true;
        console.warn('[posthog] call failed; analytics disabled for this session', err);
      }
      this.isEnabled = false;
    }
  }
}
